/**
 * Sortable column definitions for the Buying Table.
 *
 * Extracted from BuyingTable.vue so the list can be unit-tested without
 * instantiating the full Vue component.
 *
 * Ordering is meaningful — it's the order shown in the ReportSort dropdown.
 * The three "urgency" columns (Available Quantity, Days of Stock Remaining,
 * Reorder Date) appear first and override the data-type default direction to
 * ASC, so the most-urgent items surface at the top on first selection
 * (Stocky parity for purchasing workflows).
 */

import { SortableColumn } from '@/types/sort';

export const BUYING_TABLE_SORTABLE_COLUMNS: SortableColumn[] = [
  // --- Inventory urgency (asc surfaces out-of-stock / due-soon items first) ---
  {
    code: '[Actual Inventory Quantity]',
    name: 'Available Quantity',
    dataType: 'number',
    sortable: true,
    defaultDirection: 'asc',
  },
  {
    code: '[stock_days_inventory_planning]',
    name: 'Days of Stock Remaining',
    dataType: 'number',
    sortable: true,
    defaultDirection: 'asc',
  },
  {
    code: '[reorder_date]',
    name: 'Reorder Date',
    dataType: 'date',
    sortable: true,
    defaultDirection: 'asc',
  },

  // --- Reorder metrics ---
  {
    code: '[inventory_replenishment]',
    name: 'Suggested Quantity',
    dataType: 'number',
    sortable: true,
  },
  {
    code: '[incoming_inventory_quantity]',
    name: 'Incoming Inventory',
    dataType: 'number',
    sortable: true,
  },
  {
    code: '[average_daily_units_sold_param]',
    name: 'Units Sold per Day',
    dataType: 'number',
    sortable: true,
  },
  {
    code: '[sold_quantity_param]',
    name: 'Units Sold',
    dataType: 'number',
    sortable: true,
  },
  {
    code: '[unit_cost]',
    name: 'Unit Cost',
    dataType: 'number',
    sortable: true,
  },

  // --- Product / variant info (text → defaults to asc alphabetically) ---
  {
    code: 'Item[Item Description]',
    name: 'Product',
    dataType: 'text',
    sortable: true,
  },
  {
    code: 'variant',
    name: 'Variant',
    dataType: 'text',
    sortable: true,
  },
  {
    code: 'product_sku',
    name: 'SKU',
    dataType: 'text',
    sortable: true,
  },
  {
    code: 'product_barcode',
    name: 'Barcode',
    dataType: 'text',
    sortable: true,
  },
  {
    code: 'supplier',
    name: 'Supplier',
    dataType: 'text',
    sortable: true,
  },
];

/**
 * Column codes consumed by the productInfo header's `showSortArrow` template
 * helper. Lighting up the sort arrow on the productInfo column when ANY of
 * these codes is the active sort. Note: `product_barcode` is the canonical
 * code (not the legacy `barcode` alias that previously slipped into the list).
 */
export const BUYING_TABLE_PRODUCT_INFO_SORT_CODES: string[] = [
  'Item[Item Description]',
  'variant',
  'product_sku',
  'product_barcode',
  'supplier',
  'variant_id',
];
